import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Helpers } from '@recruitee/common';
import { TimeFormat } from '@recruitee/time';

import { environment } from '../../environments/environment';
import { CompanyDataResponse, CompanySize, RecruitmentInvolvement } from '../company/company.types';
import { LocalizationSettings } from '../onboarding.types';
import { UserDataResponse } from '../user/user.types';

type CompanyRequest = {
  emailConfirmation: {
    data: {
      companyLanguage: string;
      companySize: CompanySize;
      adminRole: string;
      familiarWithAts: boolean;
      recruitmentInvolvement: RecruitmentInvolvement;
      invitedEmails?: string[];
      timeFormat24: boolean;
      weekStartDay: LocalizationSettings['weekStartDay'];
      invitedEmailBodyHtml?: string;
      fillWithSampleData: boolean;
    };
    token: string;
  };
};

export type UserRequest = {
  admin: {
    firstName: string;
    lastName: string;
    password?: string;
    email: string;
    phone: string;
    avatar?: File;
    timeFormat24: boolean;
    weekStartDay: LocalizationSettings['weekStartDay'];
    preferredLanguage: string;
    invitationToken: string;
  };
};

interface TrackingData extends Record<any, any> {
  company: { createdAt: number; id: number; name: string };
  companyId: number;
  companyName: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  name: string;
  phone: string;
}

export type PatchCompanyResponse = {
  code: string;
  companyId: number;
  email: string;
  trackingData: TrackingData;
};

@Injectable()
export class OnboardingApiService {
  constructor(private http: HttpClient) {}

  public patchCompany(data: CompanyRequest): Observable<PatchCompanyResponse> {
    return <Observable<PatchCompanyResponse>>(
      this.http
        .patch(environment.apiCompanyUrl, Helpers.underscoreKeys(data))
        .pipe(map(res => Helpers.camelizeKeys(res)))
    );
  }

  public createUser(data: UserRequest): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.post(environment.apiUserUrl, Helpers.underscoreKeys(data))
    );
  }

  public createSSOUser(data: Omit<UserRequest, 'password'>): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.post(environment.apiSSOUserUrl, Helpers.underscoreKeys(data))
    );
  }

  public getUserData(token: string): Observable<UserDataResponse> {
    return <Observable<UserDataResponse>>(
      this.http
        .get(`${environment.apiUserDataUrl}${token}`)
        .pipe(map(data => Helpers.camelizeKeys(data)))
    );
  }

  public getCompanyData(token: string): Observable<CompanyDataResponse> {
    return <Observable<CompanyDataResponse>>(
      this.http
        .get(`${environment.apiCompanyDataUrl}${token}`)
        .pipe(map(data => Helpers.camelizeKeys(data)))
    );
  }

  public getCountryCode(): Observable<string> {
    return <Observable<string>>this.http.get(environment.apiCountryCodeUrl, {
      responseType: 'text',
    });
  }

  public getDefaultLocalizationSettings(): Observable<LocalizationSettings> {
    return this.http.get(`${environment.apiLocalizationSettingsUrl}`).pipe(
      map(data => Helpers.camelizeKeys(data)),
      map(response => ({
        timeFormat: response.timeFormat24 ? TimeFormat.Hour24 : TimeFormat.Hour12,
        weekStartDay: response.weekStartDay,
      })),
    );
  }
}
